/**
 * "Applies to" picker (parity: curator's subject picker). A filter box and
 * subjects grouped by template with per-group Select all — a wrap of loose
 * checkboxes doesn't survive a few dozen subjects.
 */
import { useMemo, useState } from 'react';
import { Button, Input, Modal } from '@servicecompressionllc/sc-design-system';
import type { KnowledgeSubject } from './api.js';

export const SubjectPickerModal = ({
  open = true,
  subjects,
  selected,
  onChange,
  onClose,
}: {
  /** Drive it with `open` and keep it mounted; omitted = open (callers that mount it per use). */
  open?: boolean;
  subjects: KnowledgeSubject[];
  selected: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}) => {
  const [filter, setFilter] = useState('');

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matching = subjects.filter(
      (s) => !s.archived && (q.length === 0 || s.name.toLowerCase().includes(q)),
    );
    const byTemplate = new Map<string, KnowledgeSubject[]>();
    for (const s of matching) {
      byTemplate.set(s.templateName, [...(byTemplate.get(s.templateName) ?? []), s]);
    }
    return [...byTemplate.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [subjects, filter]);

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <Modal open={open} onOpenChange={(next) => !next && onClose()}>
      <Modal.Header>
        <Modal.Title>Applies to</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3 text-sm text-ink-muted">
          Leave empty to apply everywhere — the assistant uses it on every subject. Pick subjects to
          scope it to just those.
        </p>
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter subjects…"
          aria-label="Filter subjects"
          className="mb-3 w-full"
        />
        <div className="max-h-80 overflow-y-auto rounded border border-outline">
          {groups.length === 0 && (
            <p className="p-3 text-sm text-ink-subtle">No subjects match that.</p>
          )}
          {groups.map(([templateName, rows]) => {
            const ids = rows.map((r) => r.id);
            const allPicked = ids.every((id) => selected.includes(id));
            return (
              <div key={templateName} className="border-b border-outline p-3 last:border-b-0">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                    {templateName}
                  </span>
                  <button
                    type="button"
                    className="text-sm text-link hover:underline"
                    onClick={() =>
                      onChange(
                        allPicked
                          ? selected.filter((id) => !ids.includes(id))
                          : [...new Set([...selected, ...ids])],
                      )
                    }
                  >
                    {allPicked ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {rows.map((s) => (
                    <li key={s.id}>
                      <label className="flex items-center gap-2 text-sm text-ink">
                        <input
                          type="checkbox"
                          checked={selected.includes(s.id)}
                          onChange={() => toggle(s.id)}
                        />
                        {s.name}
                        {s.kind && <span className="text-ink-subtle">({s.kind})</span>}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};
